// Proving a second factor, or setting one up and proving it (spec/07 §6.6): one block, used where the
// console gate asks for it (`StepUp.tsx`) and where accepting an invitation to a role that needs it does
// (`InvitationAcceptPage.tsx`, ADR-0046) — so a new owner sets it up right there and goes straight back to
// accepting.
//
// A member with an authenticator proves it with a code; one without sets one up — enrolling needs their
// password, or a sign-in from the last ten minutes — and proves it with its first code, which steps the
// session up in the same call. The setup key is shown as text and as an `otpauth:` link, not as a QR code:
// drawing one needs a dependency the console does not otherwise have, and the link opens the
// authenticator on a phone.

import { useTranslation } from '@clavian/i18n/react';
import { Button, Card, TextField } from '@clavian/ui-web';
import { useState, type ReactNode } from 'react';

import { useApi } from '../api/client';
import { problemCode } from '../api/problems';
import { useSession } from '../api/queries';
import { Loading } from '../shell/layouts';
import { useAuthFailure } from './auth-errors';
import { SecondFactorForm } from './SecondFactorForm';

interface Enrolment {
  readonly uri: string;
  readonly secret: string;
  readonly backupCodes: readonly string[];
}

export interface TwoFactorSetupProps {
  /** The heading's level: `1` on a page of its own, `2` inside another. */
  readonly headingLevel: 1 | 2;
  /** The session now has its second factor. */
  readonly onVerified: () => Promise<void> | void;
}

export function TwoFactorSetup({ headingLevel, onVerified }: TwoFactorSetupProps): ReactNode {
  const { t } = useTranslation('console');
  const failureText = useAuthFailure();
  const api = useApi();
  const session = useSession();
  const [enrolment, setEnrolment] = useState<Enrolment | null>(null);
  const [password, setPassword] = useState('');
  const [failure, setFailure] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);

  const enrol = async () => {
    setFailure(null);
    setStarting(true);
    try {
      const result = await api.enableTwoFactor({ body: password === '' ? {} : { password } });
      setEnrolment({
        uri: result.totpURI,
        secret: new URL(result.totpURI).searchParams.get('secret') ?? '',
        backupCodes: result.backupCodes,
      });
    } catch (error) {
      setFailure(problemCode(error) === 'SESSION_NOT_FRESH' ? t('secondFactor.enrol.staleSession') : failureText(error));
    } finally {
      setStarting(false);
    }
  };

  if (session.isPending) return <Loading />;
  const enrolled = session.data?.user.twoFactorEnabled === true;
  const Heading = headingLevel === 1 ? 'h1' : 'h2';
  const proven = async () => {
    await onVerified();
  };

  return (
    <>
      <Heading>{enrolled || enrolment !== null ? t('secondFactor.title') : t('secondFactor.enrol.title')}</Heading>
      {enrolled ? (
        <SecondFactorForm intro={t('secondFactor.stepUpIntro')} onVerified={proven} />
      ) : enrolment === null ? (
        <form
          className="console-form"
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            void enrol();
          }}
        >
          <p>{t('secondFactor.enrol.intro')}</p>
          {failure ? (
            <p className="console-alert" role="alert">
              {failure}
            </p>
          ) : null}
          <TextField
            label={t('secondFactor.enrol.password')}
            description={t('secondFactor.enrol.passwordHint')}
            type="password"
            autoComplete="current-password"
            showOptional
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
          <Button type="submit" variant="primary" busy={starting}>
            {t('secondFactor.enrol.start')}
          </Button>
        </form>
      ) : (
        <>
          <Card title={t('secondFactor.enrol.key')} titleAs={headingLevel === 1 ? 'h2' : 'h3'} description={t('secondFactor.enrol.keyHint')}>
            <p className="console-code">{enrolment.secret}</p>
            <p>
              <a href={enrolment.uri}>{t('secondFactor.enrol.openLink')}</a>
            </p>
          </Card>
          <Card title={t('secondFactor.enrol.backupCodes')} titleAs={headingLevel === 1 ? 'h2' : 'h3'} description={t('secondFactor.enrol.backupCodesHint')}>
            <ul className="console-codes">
              {enrolment.backupCodes.map((code) => (
                <li key={code} className="console-code">
                  {code}
                </li>
              ))}
            </ul>
          </Card>
          <SecondFactorForm intro={t('secondFactor.intro')} onVerified={proven} />
        </>
      )}
    </>
  );
}
